import { Component } from "react"

import { MAX_LENGTH_QUERYSET_VIZ_CAP, MIDDLE_PURPLE } from "shared/imports/sharedConstants"

import { usRegions, euRegions } from "shared/imports/regionConstants"

import { euMapConfig, federalMapConfig } from "search-new/components/Visualization/VisualizationWrappers/MapConfigs"

import {
    genericBarPieScene,
    genericLineScene,
    genericMapScene,
} from "search-new/containers/Visualization/VisualizationScene/VisualizationScenes"

import { RegionFilter, RegionCountryEUFilter } from "shared/search/containers/Filter/Filters"

import {
    NoteCreatedEndDateFilter,
    NoteCreatedStartDateFilter,
    NoteCreatorFilter,
    NoteDonationTypeFilter,
    NoteInteractionTypeFilter,
    NoteInvolvingMemberFilter,
    NoteInvolvingOfficeFilter,
    NoteInvolvingOrganizationFilter,
    NoteInvolvingPressContactFilter,
    NoteInvolvingSupporterFilter,
    NoteInvolvingUserFilter,
    NoteProjectFilter,
    NoteUserOrganizationFilter,
} from "shared/search/containers/Filter/NoteFilters"

/* *****************************************************************
 * MAPS
 ******************************************************************* */
export class NoteEUMapScene extends Component {
    static defaultProps = {
        uniqueVizSceneId: "euMapScene",
        filterId: RegionCountryEUFilter.defaultProps.uniqueFilterId,
        label: "Map",
        icon: "fa-location-arrow",
        configs: euMapConfig,
        regions: euRegions,
    }

    render() {
        return genericMapScene(this.props)
    }
}

export class NoteUSMapScene extends Component {
    static defaultProps = {
        uniqueVizSceneId: "notesUsMapScene",
        filterId: RegionFilter.defaultProps.uniqueFilterId,
        shouldIgnoreFilters: false,
        label: "Map",
        icon: "fa-globe",
        configs: federalMapConfig,
        regions: usRegions,
    }

    render() {
        return genericMapScene(this.props)
    }
}

/* *****************************************************************
 * BAR / PIE COMBO GRAPHS
 ******************************************************************* */
export class NoteTeamBarPieScene extends Component {
    static defaultProps = {
        uniqueVizSceneId: "noteTeamBarPieScene",
        filterId: NoteInvolvingUserFilter.defaultProps.uniqueFilterId,
        variableRequestKey: "teamMemberFrequency",
        label: "Participants",
        icon: "fa-users",
        sidebarTableLabel: "Team Members",
    }

    render() {
        return genericBarPieScene(this.props)
    }
}

export class NoteCreatorBarPieScene extends Component {
    static defaultProps = {
        uniqueVizSceneId: "noteCreatorBarPieScene",
        filterId: NoteCreatorFilter.defaultProps.uniqueFilterId,
        variableRequestKey: "creatorFrequency",
        label: "Creators",
        icon: "fa-user",
        sidebarTableLabel: "Note Creators",
    }

    render() {
        return genericBarPieScene(this.props)
    }
}

export class NoteOfficeBarPieScene extends Component {
    static defaultProps = {
        uniqueVizSceneId: "noteOfficeBarPieScene",
        filterId: NoteInvolvingOfficeFilter.defaultProps.uniqueFilterId,
        variableRequestKey: "officeFrequency",
        label: "Offices",
        icon: "fa-briefcase",
        sidebarTableLabel: "Involved Offices",
        permissions: [
            DjangIO.app.models.PermissionType.legislators,
            DjangIO.app.models.PermissionType.executives,
            DjangIO.app.models.PermissionType.staffers,
        ],
    }

    render() {
        return genericBarPieScene(this.props)
    }
}

// we allow visualizations by party, but no filtering on it! Hence,
// filterId is null. This just means that when a user clicks on
// the a bar, nothing will happen (it will short-circuit in BarPieWrapper.js)
export class NotePartyBarPieScene extends Component {
    static defaultProps = {
        uniqueVizSceneId: "notePartyBarPieScene",
        filterId: null,
        variableRequestKey: "partyFrequency",
        label: "Party",
        icon: "fa-check-square-o",
        colorConfigs: {
            secondaryColor: MIDDLE_PURPLE,
            colorFunc: (d, i) => DjangIO.app.person.types.Party.by_value(d.id).color,
        },
        sidebarTableLabel: "Party Affiliation",
        noOrgDesign: true,
        permissions: [
            DjangIO.app.models.PermissionType.legislators,
            DjangIO.app.models.PermissionType.executives,
            DjangIO.app.models.PermissionType.staffers,
        ],
    }

    render() {
        return genericBarPieScene(this.props)
    }
}

export class NoteStateBarPieScene extends Component {
    static defaultProps = {
        uniqueVizSceneId: "noteStateBarPieScene",
        filterId: RegionFilter.defaultProps.uniqueFilterId,
        variableRequestKey: "stateFrequency",
        label: "States",
        icon: "fa-map-marker",
        regions: usRegions,
        sidebarTableLabel: "Involved States",
    }

    render() {
        return genericBarPieScene(this.props)
    }
}

export class NoteTypeBarPieScene extends Component {
    static defaultProps = {
        uniqueVizSceneId: "noteTypeBarPieScene",
        filterId: NoteInteractionTypeFilter.defaultProps.uniqueFilterId,
        variableRequestKey: "typeFrequency",
        label: "Types",
        icon: "fa-pencil-square-o",
        sidebarTableLabel: "Note Type",
    }

    render() {
        return genericBarPieScene(this.props)
    }
}

export class NoteDonationTypeBarPieScene extends Component {
    static defaultProps = {
        uniqueVizSceneId: "noteDonationTypeBarPieScene",
        filterId: NoteDonationTypeFilter.defaultProps.uniqueFilterId,
        variableRequestKey: "donationTypeFrequency",
        label: "Donation Types",
        // https://github.com/QuorumUS/quorum-site/pull/15682/files#diff-413e9aa8961126d20e2d90befa13e0f1R10773
        icon: "fa-gift",
        sidebarTableLabel: "Donation Note Type",
    }

    render() {
        return genericBarPieScene(this.props)
    }
}

export class NoteMemberBarPieScene extends Component {
    static defaultProps = {
        uniqueVizSceneId: "noteMemberBarPieScene",
        filterId: NoteInvolvingMemberFilter.defaultProps.uniqueFilterId,
        variableRequestKey: "memberFrequency",
        label: "Officials",
        icon: "fa-user",
        sidebarTableLabel: "Officials",
        permissions: [
            DjangIO.app.models.PermissionType.legislators,
            DjangIO.app.models.PermissionType.executives,
            DjangIO.app.models.PermissionType.staffers,
        ],
    }

    render() {
        return genericBarPieScene(this.props)
    }
}

export class NotePressContactBarPieScene extends Component {
    static defaultProps = {
        uniqueVizSceneId: "notePressContactBarPieScene",
        filterId: NoteInvolvingPressContactFilter.defaultProps.uniqueFilterId,
        variableRequestKey: "pressContactFrequency",
        label: DjangIO.app.models.QuorumDataType.press_contact.plural,
        icon: "fa-microphone",
        sidebarTableLabel: DjangIO.app.models.QuorumDataType.press_contact.plural,
        permissions: [DjangIO.app.models.PermissionType.media_relations],
    }

    render() {
        return genericBarPieScene(this.props)
    }
}

export class NotePublicOrganizationBarPieScene extends Component {
    static defaultProps = {
        uniqueVizSceneId: "notePublicOrganizationBarPieScene",
        filterId: NoteInvolvingOrganizationFilter.defaultProps.uniqueFilterId,
        variableRequestKey: "publicOrganizationFrequency",
        label: "Organizations",
        icon: "fa-building",
        sidebarTableLabel: "Public Organizations",
    }

    render() {
        return genericBarPieScene(this.props)
    }
}

export class NoteOrganizationBarPieScene extends Component {
    static defaultProps = {
        uniqueVizSceneId: "noteOrganizationBarPieScene",
        filterId: NoteUserOrganizationFilter.defaultProps.uniqueFilterId,
        variableRequestKey: "organizationFrequency",
        label: "Consultants",
        icon: "fa-handshake-o",
        permissions: [DjangIO.app.models.PermissionType.consultants],
        sidebarTableLabel: "Consultant Organizations",
    }

    render() {
        return genericBarPieScene(this.props)
    }
}

export class NoteSupporterBarPieScene extends Component {
    static defaultProps = {
        uniqueVizSceneId: "noteSupporterBarPieScene",
        filterId: NoteInvolvingSupporterFilter.defaultProps.uniqueFilterId,
        variableRequestKey: "contactFrequency",
        label: "Contacts",
        icon: "fa-child",
        permissions: [DjangIO.app.models.PermissionType.custom_contacts],
        maxDataCount: MAX_LENGTH_QUERYSET_VIZ_CAP,
        sidebarTableLabel: "Contacts",
    }

    render() {
        return genericBarPieScene(this.props)
    }
}

export class NoteProjectBarPieScene extends Component {
    static defaultProps = {
        uniqueVizSceneId: "noteProjectBarPieScene",
        filterId: NoteProjectFilter.defaultProps.uniqueFilterId,
        variableRequestKey: "inIssueFrequency",
        label: "Issues",
        icon: "fa-list-alt",
        sidebarTableLabel: "Issues",
    }

    render() {
        return genericBarPieScene(this.props)
    }
}

export class NotePartyInternationalBarPieScene extends Component {
    static defaultProps = {
        uniqueVizSceneId: "notePartyInternationalBarPieScene",
        filterId: null,
        variableRequestKey: "internationalPartyFrequency",
        label: "Parties",
        icon: "fa-check-square-o",
        maxDataCount: MAX_LENGTH_QUERYSET_VIZ_CAP,
        sidebarTableLabel: "Mentions by Party",
    }

    render() {
        return genericBarPieScene(this.props)
    }
}

/* *****************************************************************
 * LINE GRAPH (TREND)
 ******************************************************************* */
export class NoteTrendsScene extends Component {
    static defaultProps = {
        uniqueVizSceneId: "noteTrendsScene",
        dateStartId: NoteCreatedStartDateFilter.defaultProps.uniqueFilterId,
        dateEndId: NoteCreatedEndDateFilter.defaultProps.uniqueFilterId,
        label: "Created",
        icon: "fa-line-chart",
    }

    render() {
        return genericLineScene(this.props)
    }
}
